/**
 * What to do:
 * Move logic from GameController to AttackController, this includes the rarities of the attacks
 *
 */

import AircraftController from "../aircraft/AircraftController";
import { FLIGHT_CATEGORY } from "../constants/aircraftConstants";
import { ATTACK_SETTING_NAMES, GAME_ATTACK_TYPES } from "../constants/gameAttackConstants";
import EventBus from "../lib/EventBus";
import SpawnPatternCollection from "../trafficGenerator/SpawnPatternCollection";

import FalseHeadningAttack from "./FalseHeadingAttack";
import FalseInformationAttack from "./FalseInformationAttack";
import FalseSqwakAttack from "./FalseSqwakAttack";
import NonMovingAttack from "./NonMovingAttack";
import NonResponsiveAttack from "./NonResponsiveAttack";
import PositionAlteringAttack from "./PositionAlteringAttack";
import ImpersonationAttack from "./ImpersonationAttack";
import DuplicationAttack from "./DuplicationAttack";

import TimeKeeper from "../engine/TimeKeeper";
import GameAttacks from "../game/GameAttacks";
import AircraftModel from "../aircraft/AircraftModel";
import UiController from "../ui/UiController";
import { ATTACK_CONSTANTS, ATTACK_INFORMATION, ATTACK_SCORE_MAP, ATTACKED_AIRCRAFT_ON_ARRIVAL} from "../constants/attackConstants";
import GameController, { GAME_EVENTS } from "../game/GameController";
import TestController from "../testModule/TestController";
import { EVENT } from "../constants/eventNames";
import SybilAttack from "./SybilAttack";
import DroneAttack from "./DroneAttack";
import { DroneAttackController, DroneAttackModel} from "./DroneAttackController";
import GPSAttack from "./GPSAttack";
import { GNSSAttackController, GNSSAttackModel } from "./GNSSAttackController";

class AttackController {
    constructor() {

        /**
         * The number of aircraft that is affected by each attack
         *
         * @type {Object}
         * @property {number} int
         */
        this.aircraftAffectedByAttack = {
            [GAME_ATTACK_TYPES.REGULAR]: [],
            [GAME_ATTACK_TYPES.NON_RESPONSIVE]: [],
            [GAME_ATTACK_TYPES.POSITION_ALTERING]: [],
            [GAME_ATTACK_TYPES.FALSE_INFORMATION]: [],
            [GAME_ATTACK_TYPES.NON_MOVING]: [],
            [GAME_ATTACK_TYPES.FALSE_SQUAWK]: [],
            [GAME_ATTACK_TYPES.FALSE_HEADING]: [],
            [GAME_ATTACK_TYPES.DUPLICATED_AIRCRAFT]: [],
            [GAME_ATTACK_TYPES.IMPERSONATED_AIRCRAFT]: [],
            [GAME_ATTACK_TYPES.SYBIL]: [],
            [GAME_ATTACK_TYPES.REPLAY_ATTACK]: [],
            [GAME_ATTACK_TYPES.GPS_JAMMING]: [],
            [GAME_ATTACK_TYPES.DRONE_ATTACK]: []
        };

        /**
         * Keep track of the flooding aircraft
         *
         * @type {Array.<AircraftModel>}
         * @property
         * @default []
         */
        this.floodingAircrafts = [];

        /**
         * The handler for the attack options
         *
         * @type {GameAttacks}
         * @property
         */
        this.attackOptions = new GameAttacks();

        /**
         * Attack injection queue
         *
         * @type {Array.<number>}
         * @property
         * @default []
         */
        this.attackInjectionQueue = [];

        /**
         * Event bus
         *
         * @type {EventBus}
         * @property
         * @private
         */
        this._eventBus = EventBus;
    }

    get maximumOfSybilAircraft() {
        return this.getAttackOption(ATTACK_SETTING_NAMES.MAXIMUM_OF_SYBIL_AIRCRAFT);
    }

    get amountOfSybilNodes() {
        return this.getAttackOption(ATTACK_SETTING_NAMES.AMOUNT_OF_SYBIL_NODES);
    }

    init_pre() {
        this.setUpHandlers()
            .enable();
    }

    destroy() {
        this.disable();
        this.reset();
    }

    /**
     * Initialize the AttackController
     *
     * @param {AircraftController} aircraftController
     */
    init(aircraftController) {
        this._aircraftController = aircraftController;
        this._gnssAttackController = new GNSSAttackController();
        this._droneAttackController = new DroneAttackController();
    }

    /**
     * Enables the events used by the AttackController
     *
     * @method enable
     * @memberof AttackController
     * @instance
     * @chainable
     */
    enable() {
        this._eventBus.on(EVENT.SET_FLOODING_NON_RESPONSIVE, this._updateFloodingOfAircraftHandler);
        this._eventBus.on(EVENT.TOGGLE_SCORE_DISPLAY, this._toggleScoreDisplayHandler);
        this._eventBus.on(EVENT.SPAWN_SYBIL_NODES, this._spawnSybilNodesHandler);
        this._eventBus.on(EVENT.TOGGLE_GPS_JAM, this._toggleGPSJammingHandler);
        return this;
    }

    /**
     * Disables the events used by the AttackController
     *
     * @method disable
     * @memberof AttackController
     * @instance
     * @chainable
     */
    disable() {
        this._eventBus.off(EVENT.SET_FLOODING_NON_RESPONSIVE, this._updateFloodingOfAircraftHandler);
        this._eventBus.off(EVENT.TOGGLE_SCORE_DISPLAY, this._toggleScoreDisplayHandler);
        this._eventBus.off(EVENT.SPAWN_SYBIL_NODES, this._spawnSybilNodesHandler);
        this._eventBus.off(EVENT.TOGGLE_GPS_JAM, this._toggleGPSJammingHandler);
        return this;
    }

    /**
     * Set up the event handlers for the AttackController
     *
     * @method setUpHandlers
     * @memberof AttackController
     * @instance
     * @chainable
     */
    setUpHandlers() {
        this._updateFloodingOfAircraftHandler = this.updateFloodingOfAircraft.bind(this);
        this._toggleScoreDisplayHandler = this.toggleDisplayOfScoreInUI.bind(this);
        this._spawnSybilNodesHandler = this.spawnSybilNodes.bind(this);
        this._toggleGPSJammingHandler = this.toggleGPSJamming.bind(this);
        return this;
    }

    /**
     * Return and remove the earliest attack in the injection queue
     *
     * @method getLatestAttackInQueue
     * @memberof AttackController
     * @instance
     * @public
     */
    getLatestAttackInQueue() {
        return this.attackInjectionQueue.shift();
    }

    /**
     * Add an attack to the injection queue
     *
     * @method addAttackToQueue
     * @memberof AttackController
     * @instance
     * @public
     */
    addAttackToQueue(attackType) {
        this.attackInjectionQueue.push(attackType);
    }

    /**
     * Reset the attack settings
     * Facade for `attackOptions.reset`
     *
     * @method resetAttackSettings
     * @memberof AttackController
     * @instance
     * @public
     */
    resetAttackSettings() {
        this.attackOptions.reset();
    }

    /**
     * Facade for `attacOptions.get`
     *
     * Allows for classes that import the `AttackController` single-level
     * access to any game option value
     *
     * @for GameController
     * @method getGameOption
     * @param optionName {string}
     * @return {string}
     */
    getAttackOption(optionName) {
        return this.attackOptions.getAttackOptionByName(optionName);
    }

    calculateScalingFactor() {
        const maxProbaility = 1;

        let sumOfProbabilities = 0;

        for (const attackType in this.attackProbabilities) {
            sumOfProbabilities += this.attackProbabilities[attackType];
        }

        if (sumOfProbabilities <= 1) {
            return 1;
        }

        const scalingFactor = maxProbaility / sumOfProbabilities;

        return scalingFactor;
    }

    validateAttackType(attackType) {
        const attackTypes = Object.values(GAME_ATTACK_TYPES).filter((type) => type !== GAME_ATTACK_TYPES.REGULAR);

        return attackTypes.includes(attackType);
    }

    reset() {
        for (const attack in this.aircraftAffectedByAttack) {
            this.aircraftAffectedByAttack[attack] = [];
        }
        // Reset drone attacks
        this._droneAttackController.setDroneAttacks([]);
    }

    /**
     * Updates the attack data of the aircraft, and if the aircraft is on its way to final fix the attack type
     * is of the removal kind the aircraft is removed from the simulation.
     */
    updateAircraft(aircraftModel) {
        this._droneAttackController.update(aircraftModel);
        this._gnssAttackController.updateAircraft(aircraftModel);

        const attackType = aircraftModel.attackType;

        if (!this.validateAttackType(attackType)) {
            return;
        }

        const lastWaypoint = aircraftModel.fms.waypoints[aircraftModel.fms.waypoints.length - 1];
        const currentWaypoint = aircraftModel.fms.currentWaypoint;

        if (lastWaypoint === currentWaypoint) {
            const shouldIBeRemoved = ATTACK_INFORMATION[aircraftModel.attackType].onArrival.includes(ATTACKED_AIRCRAFT_ON_ARRIVAL.AIRCRAFT_DISEPEARS);

            if (shouldIBeRemoved) {
                console.log('Aircraft removed from simulation', aircraftModel.callsign, " it was attacked with ", aircraftModel.attackType);

                UiController.ui_log(`Aircraft removed from simulation ${aircraftModel.callsign}, due to being attacked`, true);

                this._aircraftController.aircraft_remove(aircraftModel);

            }

            return;
        }




        // TODO: Move attack update here
    }

    /**
     * Get the injection data for a new aircraft, based on the probabilty that a new aircraft is attacked
     *
     * @param {string} flightCategory
     * @param {string} newOrExistingAircraft
     * @returns
     */
    getInjectionDataForNewAircraft(flightCategory) {
        const probabilty = this.getAttackOption(ATTACK_SETTING_NAMES.PROBABILITY_OF_NEW_AIRCRAFT);
        const newOrExistingAircraft = ATTACK_CONSTANTS.NEW_AIRCRAFT;

        if (this.attackInjectionQueue.length > 0) {
            const attackType = this.getLatestAttackInQueue();
            if (this.isAttackCompatibleWithAircraftType(attackType, newOrExistingAircraft) && this.isAttackCompatibleWithFlightCategory(attackType, flightCategory)) {
                return this.buildAttackInjectionData(attackType);
            }
            // Add the attack in the queue back to the queue in the front
            this.attackInjectionQueue.unshift(attackType);
        }

        if (Math.random() < probabilty) {
            return this.getRandomAttackInjectionData(flightCategory, newOrExistingAircraft);
        }

        return this.buildAttackInjectionData(GAME_ATTACK_TYPES.REGULAR);
    }

    /**
     * Check if the given attack type is compatible with the given flight category
     *
     * @param {number} attackType
     * @param {string} flightCategory
     * @returns {boolean}
     * @method isAttackCompatibleWithFlightCategory
     */
    isAttackCompatibleWithFlightCategory(attackType, flightCategory) {
        if (!this.validateAttackType(attackType)) {
            return false;
        }

        if (typeof flightCategory !== 'string' || !Object.values(FLIGHT_CATEGORY).includes(flightCategory)) {
            return false;
        }

        const attackInfo = ATTACK_INFORMATION[attackType];

        return attackInfo.appliesToFlightCategory.includes(flightCategory);
    }

    /**
     * Check if the given attack type is compatible with the given aircraft type
     *
     * @param {number} attackType
     * @param {string} newOrExistingAircraft
     * @returns {boolean}
     * @method isAttackCompatibleWithAircraftType
     */
    isAttackCompatibleWithAircraftType(attackType, newOrExistingAircraft) {
        if (!this.validateAttackType(attackType)) {
            return false;
        }

        if (typeof newOrExistingAircraft !== 'string' || !Object.values(ATTACK_CONSTANTS).includes(newOrExistingAircraft)) {
            return false;
        }

        const attackInfo = ATTACK_INFORMATION[attackType];

        return attackInfo.appliesTo.includes(newOrExistingAircraft);
    }


    /**
     * Get a random attack type based on the probabilty of the attacks
     *
     * @method getRandomAttackInjectionData
     * @memberof AttackController
     * @param {string} flightCategory
     * @param {string} newOrExistingAircraft
     */
    getRandomAttackInjectionData(flightCategory, newOrExistingAircraft) {
        const attackType = this.getRandomAttackType(flightCategory, newOrExistingAircraft);

        return this.buildAttackInjectionData(attackType);
    }

    /**
     * Returns a random attack type based on the probabilty of the attacks
     *
     * @returns {number}
     * @method getRandomAttackType
     * @memberof AttackController
     * @param {string} flightCategory
     * @param {string} newOrExistingAircraft
     */
    getRandomAttackType(flightCategory, newOrExistingAircraft) {
        if (typeof flightCategory !== 'string' || !Object.values(FLIGHT_CATEGORY).includes(flightCategory)) {
            throw new Error('Invalid flight category.');
        }

        if (typeof newOrExistingAircraft !== 'string' || !Object.values(ATTACK_CONSTANTS).includes(newOrExistingAircraft)) {
            throw new Error(`Invalid newOrExistingAircraft`);
        }

        let probabilities = [];
        let applicableAttacks = [];

        // Filter the attacks based on flight category and aircraft type
        Object.keys(ATTACK_INFORMATION).forEach(attack => {
            const attackInfo = ATTACK_INFORMATION[attack];

            if (Number(attack) === GAME_ATTACK_TYPES.SYBIL) {
                if (this.getAmountOfAircraftAffectedByAttack(GAME_ATTACK_TYPES.SYBIL) >= this.maximumOfSybilAircraft) {
                    return;
                }
            }

            if (Number(attack) === GAME_ATTACK_TYPES.DRONE_ATTACK) {
                // Only allow one drone attack at a time
                if (this.getAmountOfAircraftAffectedByAttack(GAME_ATTACK_TYPES.DRONE_ATTACK) >= 1) {
                    return;
                }
            }

            if (attackInfo.appliesToFlightCategory.includes(flightCategory) &&
                attackInfo.appliesTo.includes(newOrExistingAircraft)) {
                const probability = this.getAttackOption(attackInfo.settingName);
                if (probability > 0) {
                    applicableAttacks.push(parseInt(attack, 10));
                    probabilities.push(probability);
                }
            }
        });

        // If no applicable attacks, return regular attack type
        if (applicableAttacks.length === 0) {
            return GAME_ATTACK_TYPES.REGULAR;
        }

        // Normalize probabilities if the sum > 1
        const sum = probabilities.reduce((a, b) => a + b, 0);
        if (sum > 1) {
            probabilities = probabilities.map(prob => prob / sum);
        }

        // Select a random index based on the probabilities
        const randomNum = Math.random();
        let cumulativeProbability = 0;

        for (let i = 0; i < probabilities.length; i++) {
            cumulativeProbability += probabilities[i];
            if (cumulativeProbability >= randomNum) {
                return applicableAttacks[i];
            }
        }

        // Fallback in case of rounding issues, return the last applicable attack
        const lastAttack = applicableAttacks[applicableAttacks.length - 1];
        console.log(lastAttack);
        return lastAttack;
    }

    applyGPSJammingToAircraft(aircraftModel, highPower) {
        aircraftModel.GPSAttack = new GPSAttack(aircraftModel, highPower, TimeKeeper.accumulatedDeltaTime);
        this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.GPS_JAMMING].push(aircraftModel);
        TestController.LOG_New_Attacked_Aircraft(aircraftModel);
    }

    /**
     * Applies the attack to the aircraft model, based on the attack type in aircraftModel
     *
     * @param {AircraftModel} aircraftModel
     * @method applyAttackToAircraftModel
     * @memberof AttackController
     * @instance
     */
    applyAttackToAircraftModel(aircraftModel) {
        const attackType = aircraftModel.attackType;

        if (!this.validateAttackType(attackType)) {
            return;
        }

        switch (attackType) {
            case GAME_ATTACK_TYPES.NON_RESPONSIVE:
                aircraftModel.NonResponsiveAttack = new NonResponsiveAttack(TimeKeeper.accumulatedDeltaTime);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.NON_RESPONSIVE].push(aircraftModel);
                break;
            case GAME_ATTACK_TYPES.FALSE_SQUAWK:
                aircraftModel.FalseSqwakAttack = new FalseSqwakAttack(TimeKeeper.accumulatedDeltaTime, aircraftModel.transponderCode);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.FALSE_SQUAWK].push(aircraftModel);
                break;
            case GAME_ATTACK_TYPES.FALSE_INFORMATION:
                aircraftModel.FalseInformationAttack = new FalseInformationAttack(TimeKeeper.accumulatedDeltaTime);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.FALSE_INFORMATION].push(aircraftModel);
                break;
            case GAME_ATTACK_TYPES.FALSE_HEADING:
                aircraftModel.FalseHeadingAttack = new FalseHeadningAttack(TimeKeeper.accumulatedDeltaTime, aircraftModel.heading);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.FALSE_HEADING].push(aircraftModel);
                break;
            case GAME_ATTACK_TYPES.NON_MOVING:
                aircraftModel.NonMovingAttack = new NonMovingAttack(TimeKeeper.accumulatedDeltaTime);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.NON_MOVING].push(aircraftModel);
                break;
            case GAME_ATTACK_TYPES.POSITION_ALTERING:
                aircraftModel.PositionAlteringAttack = new PositionAlteringAttack(TimeKeeper.accumulatedDeltaTime);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.POSITION_ALTERING].push(aircraftModel);
                break;
            case GAME_ATTACK_TYPES.DUPLICATED_AIRCRAFT: {
                const aircraftToAttack = aircraftModel.attackInjectionData.aircraftToAttack;
                aircraftModel.DuplicationAircraftAttack = new DuplicationAttack(TimeKeeper.accumulatedDeltaTime, aircraftModel, aircraftToAttack);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.DUPLICATED_AIRCRAFT].push(aircraftModel);
                break;
            }
            case GAME_ATTACK_TYPES.IMPERSONATED_AIRCRAFT: {
                const aircraftToAttack = aircraftModel.attackInjectionData.aircraftToAttack;
                aircraftModel.ImpersonationAttack = new ImpersonationAttack(TimeKeeper.accumulatedDeltaTime, aircraftToAttack);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.IMPERSONATED_AIRCRAFT].push(aircraftModel);
                break;
            }
            case GAME_ATTACK_TYPES.SYBIL:
                aircraftModel.SybilAttack = new SybilAttack(TimeKeeper.accumulatedDeltaTime);
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.SYBIL].push(aircraftModel);
                break;
            case GAME_ATTACK_TYPES.GPS_JAMMING:
                assert(false); // Should not happen here
                break;
            case GAME_ATTACK_TYPES.REPLAY_ATTACK:
                //TODO: Not created yet @jonsv200
                break
            case GAME_ATTACK_TYPES.DRONE_ATTACK:
                aircraftModel.DroneAttack = new DroneAttack(TimeKeeper.accumulatedDeltaTime);
                this._droneAttackController.setDroneAttacks(this._buildDroneAttack(aircraftModel));
                this.aircraftAffectedByAttack[GAME_ATTACK_TYPES.DRONE_ATTACK].push(aircraftModel);
                break;
        }

        //TODO: Log that a new aircraft has been attacked
        TestController.LOG_New_Attacked_Aircraft(aircraftModel);
    }

    /**
     * Injects a new attacked aircraft into the simulation.
     * The aircraft is injected on a random spawn pattern that is not too close to any other aircraft.
     *
     * @for SpawnScheduler
     * @method directInjectAttackedAircraft
     * @param attackType {number} the type of attack to use for the new aircraft
     */
    directInjectAttackedAircraft(attackType) {
        // Ensure the attackType is valid
        if (typeof attackType !== 'number' || !this.validateAttackType(attackType)) {
            throw new Error('Invalid attack type.');
        }

        // Get a random SpawnPatternModel from SpawnPatternCollection
        const spawnPatternModels = SpawnPatternCollection.findSpawnPatternsByCategory(FLIGHT_CATEGORY.ARRIVAL);
        const availableSpawnPatternModels = spawnPatternModels.length;
        const SAFE_RADIUS = this.attackOptions.getAttackOptionByName(ATTACK_SETTING_NAMES.SAFE_RADIUS_FOR_DIRECT_INJECTION);

        for (let attempt = 0; attempt < availableSpawnPatternModels; attempt++) {
            const randomIndex = Math.floor(Math.random() * spawnPatternModels.length);
            const spawnPatternModel = spawnPatternModels[randomIndex];
            const firstPosition = spawnPatternModel.positionModel.relativePosition; // Fetches the first position of the SpawnPatternModel

            const [nearestAircraft, distance] = this._aircraftController.aircraft_get_nearest(firstPosition);

            console.log('attempt', attempt, 'distance', distance, 'SAFE_RADIUS', SAFE_RADIUS)

            if (distance > SAFE_RADIUS) {
                // Create the attackInjectionData
                const attackInjectionData = this.buildAttackInjectionData(attackType);

                // Create the aircraft with the attackInjectionData
                this._aircraftController.createAircraftWithSpawnPatternModel(spawnPatternModel, false, attackInjectionData);
                return true;
            }
        }

        // If we've reached this point, we've failed to find a safe location after the maximum number of attempts
        return false;
    }

    /**
     * Applies attacks to the pre-spawned aircraft, based on the setting for the percentage of pre-spawned aircraft that should be attacked
     *
     * @param {Array.<AircraftModel>} preSpawnedAircraft
     */
    applyAttacksToPreSpawnedAircraft(preSpawnedAircraft) {
        const percentageOfAttackedAircraft = this.getAttackOption(ATTACK_SETTING_NAMES.PERCENTAGE_OF_PRESPAWNED_AIRCRAFT);

        const newOrExistingAircraft = ATTACK_CONSTANTS.EXISTING_AIRCRAFT;

        const amountOfPreSpawnedAircraft = preSpawnedAircraft.length;

        const amountOfAttackedAircraft = Math.ceil(amountOfPreSpawnedAircraft * percentageOfAttackedAircraft);

        for (let i = 0; i < amountOfAttackedAircraft; i++) {
            const aircraftModel = preSpawnedAircraft[i];

            const attackInjectionData = this.getRandomAttackInjectionData(aircraftModel.category, newOrExistingAircraft);

            aircraftModel.attackType = attackInjectionData.attackType;

            this.applyAttackToAircraftModel(aircraftModel);
        }
    }

    buildAttackInjectionData(attackType) {
        let aircraftToAttack = null;

        switch(attackType) {
            case GAME_ATTACK_TYPES.DUPLICATED_AIRCRAFT:
            case GAME_ATTACK_TYPES.IMPERSONATED_AIRCRAFT:
                aircraftToAttack = this.findRandomNonAttackedAircraft();
                return this.createAttackObjectForAircraft(attackType, aircraftToAttack);
            default:
                return {
                    attackType: attackType
                }
        }
    }

    /**
     * Find a random aircraft that is not attacked
     *
     * @method findRandomNonAttackedAircraft
     * @memberof AttackController
     * @instance
     * @returns {AircraftModel}
     * @public
     */
    findRandomNonAttackedAircraft() {
        const aircraft = this._aircraftController.aircraft.list;

        const nonAttackedAircraft = aircraft.filter((aircraft) => {
            return aircraft.attackType === GAME_ATTACK_TYPES.REGULAR && aircraft.isAirborne();
        });

        if (nonAttackedAircraft.length === 0) {
            return null;
        }

        const randomIndex = Math.floor(Math.random() * nonAttackedAircraft.length);

        return nonAttackedAircraft[randomIndex];
    }


    /**
     * Get all attacked aircraft.
     *
     * @method getAttackedAircraft
     * @memberof AttackController
     * @returns {Array<AircraftModel>}
     * @public
     */
    getAttackedAircraft() {
        const aircraft = this._aircraftController.aircraft.list;
        return aircraft.filter((aircraft) => this.validateAttackType(aircraft.attackType));
    }

    /**
     * Creates a attackinjectionobject for attacks that require an aircraft to attack
     *
     * @param {number} attackType
     * @param {AircraftModel} aircraftToAttack
     * @returns {Object}
     */
    createAttackObjectForAircraft(attackType, aircraftToAttack) {
        if (aircraftToAttack === null) {
            return {
                attackType: GAME_ATTACK_TYPES.REGULAR
            }
        }
        return {
            attackType: attackType,
            aircraftToAttack: aircraftToAttack
        }
    }

    /**
     * Mark the aircraft as not guessed if the aircraft has not been guessed on.
     *
     * Record the event for the no guess, if the aircraft has not been guessed on,
     * and the event has not been recorded yet.
     *
     * @memberof AttackController
     * @method markNoGuessForAircraft
     * @param {AircraftModel} aircraftModel
     */
    markNoGuessForAircraft(aircraftModel) {
        // If the aircraft has not been guessed on and it is attacked.
        const hasGuessed = aircraftModel.hasGuessed;
        const noDeductionForAttack = aircraftModel.attackType === GAME_ATTACK_TYPES.FLOODING_OF_NON_RESPONSIVE || aircraftModel.attackType === GAME_ATTACK_TYPES.DRONE_ATTACK;
        // Do not mark the aircraft as not guessed if already marked as not guessed
        const noGuessLogged = aircraftModel.noGuessLogged;

        const timeSinceAttackStart = this.getTimeSinceAttackStart(aircraftModel)
        const isGuessTooLate = timeSinceAttackStart > ATTACK_SCORE_MAP.FIFTY_POINTS.TIME_INTERVAL.MAX

        if (isGuessTooLate && !hasGuessed && !noDeductionForAttack && !noGuessLogged) {
            aircraftModel.noGuessLogged = true;
            const args = [aircraftModel]
            GameController.events_recordNew(GAME_EVENTS.NO_GUESS, ...args);
        }
    }

    endAttack(aircraftModel) {
        const attackType = aircraftModel.attackType;

        if (!this.validateAttackType(attackType)) {
            return;
        }

        this.markNoGuessForAircraft(aircraftModel);

        // If the aircraft has not been guessed on and it is been attacked.
        const hasGuessed = aircraftModel.hasGuessed;
        // Skip deduction for specific attack types
        const noDeductionForAttack = aircraftModel.attackType ===
            GAME_ATTACK_TYPES.FLOODING_OF_NON_RESPONSIVE ||
            GAME_ATTACK_TYPES.DRONE_ATTACK;

        if (!hasGuessed && !noDeductionForAttack) {
            const args = [aircraftModel]
            GameController.events_recordNew(GAME_EVENTS.NO_GUESS, ...args);
        }

        let attackDuration = null;
        let endTimestamp = TimeKeeper.accumulatedDeltaTime;

        switch (attackType) {
            case GAME_ATTACK_TYPES.NON_RESPONSIVE:
                aircraftModel.NonResponsiveAttack = null;
                break;
            case GAME_ATTACK_TYPES.FALSE_SQUAWK:
                aircraftModel.FalseSqwakAttack = null;
                break;
            case GAME_ATTACK_TYPES.FALSE_INFORMATION:
                aircraftModel.FalseInformationAttack = null;
                break;
            case GAME_ATTACK_TYPES.FALSE_HEADING:
                aircraftModel.FalseHeadingAttack = null;
                break;
            case GAME_ATTACK_TYPES.NON_MOVING:
                aircraftModel.NonMovingAttack = null;
                break;
            case GAME_ATTACK_TYPES.POSITION_ALTERING:
                aircraftModel.PositionAlteringAttack = null;
                break;
            case GAME_ATTACK_TYPES.DUPLICATED_AIRCRAFT:
                aircraftModel.DuplicationAircraftAttack = null;
                break;
            case GAME_ATTACK_TYPES.IMPERSONATED_AIRCRAFT:
                aircraftModel.ImpersonationAttack = null;
                break;
            case GAME_ATTACK_TYPES.SYBIL:
                aircraftModel.SybilAttack = null;
                break;
            case GAME_ATTACK_TYPES.GPS_JAMMING:
                aircraftModel.GPSAttack = null;
                break;
            case GAME_ATTACK_TYPES.FLOODING_OF_NON_RESPONSIVE:
                return;
            case GAME_ATTACK_TYPES.REPLAY_ATTACK:
                // TODO: Not implemented yet @jonsv200
                break;
            case GAME_ATTACK_TYPES.DRONE_ATTACK:
                aircraftModel.DroneAttack = null;
                this._droneAttackController.startRemovalOfDroneAttack(aircraftModel.id);
                break;
            default:
                break;
        }

        this.aircraftAffectedByAttack[attackType].splice(this.aircraftAffectedByAttack[attackType].indexOf(aircraftModel), 1);
    }

    getAttackStartTime(aircraftModel) {
        const attackType = aircraftModel.attackType;

        if (!this.validateAttackType(attackType)) {
            return;
        }

        switch (attackType) {
            case GAME_ATTACK_TYPES.NON_RESPONSIVE:
                return aircraftModel.NonResponsiveAttack.startTimestamp;
            case GAME_ATTACK_TYPES.FALSE_SQUAWK:
                return aircraftModel.FalseSqwakAttack.startTimestamp;
            case GAME_ATTACK_TYPES.FALSE_INFORMATION:
                return aircraftModel.FalseInformationAttack.startTimestamp;
            case GAME_ATTACK_TYPES.FALSE_HEADING:
                return aircraftModel.FalseHeadingAttack.startTimestamp;
            case GAME_ATTACK_TYPES.NON_MOVING:
                return aircraftModel.NonMovingAttack.startTimestamp;
            case GAME_ATTACK_TYPES.POSITION_ALTERING:
                return aircraftModel.PositionAlteringAttack.startTimestamp;
            case GAME_ATTACK_TYPES.DUPLICATED_AIRCRAFT:
                return aircraftModel.DuplicationAircraftAttack.startTimestamp;
            case GAME_ATTACK_TYPES.IMPERSONATED_AIRCRAFT:
                return aircraftModel.ImpersonationAttack.startTimestamp;
            case GAME_ATTACK_TYPES.SYBIL:
                return aircraftModel.SybilAttack.startTimestamp;
            case GAME_ATTACK_TYPES.REPLAY_ATTACK:
                //TODO: Not implemented yet @jonsv200
                return -1;
            case GAME_ATTACK_TYPES.DRONE_ATTACK:
                return aircraftModel.DroneAttack.startTimestamp;
            default:
                return -1;
        }
    }


    /**
     * Calculates the time since the attack started.
     *
     * @param {AircraftModel} aircraftModel
     * @returns {number}
     */
    getTimeSinceAttackStart(aircraftModel) {
        const startTimestamp = this.getAttackStartTime(aircraftModel);
        const currentTimestamp = TimeKeeper.accumulatedDeltaTime;
        return currentTimestamp - startTimestamp;
    }

    /**
     * Calculates the game event for the points based on the attack start time
     *
     * @method getGameEventForPoints
     * @memberof AttackController
     * @param {AircraftModel} aircraftModel
     * @returns
     */
    getGameEventForPoints(aircraftModel) {
        const attackType = aircraftModel.attackType;

        const scoringEnabled = ATTACK_INFORMATION[attackType].scoringEnabled;

        if (!scoringEnabled) {
            return GAME_EVENTS.NO_POINTS_FOR_GUESS
        }

        const difference = this.getTimeSinceAttackStart(aircraftModel);

        if (difference < 0) {
            return null;
        }

        for (const scoreType in ATTACK_SCORE_MAP) {
            const { TIME_INTERVAL, GAME_EVENT } = ATTACK_SCORE_MAP[scoreType];
            if (difference >= TIME_INTERVAL.MIN && difference <= TIME_INTERVAL.MAX) {
                return GAME_EVENT;
            }
        }

        // If no time interval matches, return the event for time > FIVE_MINUTES
        return ATTACK_SCORE_MAP.FIFTY_POINTS.GAME_EVENT;
    }

    /**
     * Calculate the deduction event for a given awarded event
     *
     * @param {string} awardedEvent
     * @returns
     */
    getDeductionEvent(awardedEvent) {
        switch (awardedEvent) {
            case GAME_EVENTS.ONE_MINUTE_CORRECT_GUESS:
                return GAME_EVENTS.DEDUCT_ONE_MINUTE_CORRECT_GUESS;
            case GAME_EVENTS.TWO_MINUTE_CORRECT_GUESS:
                return GAME_EVENTS.DEDUCT_TWO_MINUTE_CORRECT_GUESS;
            case GAME_EVENTS.THREE_MINUTE_CORRECT_GUESS:
                return GAME_EVENTS.DEDUCT_THREE_MINUTE_CORRECT_GUESS;
            case GAME_EVENTS.FOUR_MINUTE_CORRECT_GUESS:
                return GAME_EVENTS.DEDUCT_FOUR_MINUTE_CORRECT_GUESS;
            case GAME_EVENTS.FIVE_MINUTE_CORRECT_GUESS:
                return GAME_EVENTS.DEDUCT_FIVE_MINUTE_CORRECT_GUESS;
            case GAME_EVENTS.FIVE_MINUTE_PLUS_CORRECT_GUESS:
                return GAME_EVENTS.DEDUCT_FIVE_MINUTE_PLUS_CORRECT_GUESS;
            case GAME_EVENTS.NO_POINTS_FOR_GUESS:
                return GAME_EVENTS.NO_DEDUCTION_FOR_GUESS;
            default:
                return null; // Return null if no deduction event matches
        }
    }

    updateFloodingOfAircraft() {
        // Get the setting on how many aircraft that should flood
        const amountOfAircraft = this.getAttackOption(ATTACK_SETTING_NAMES.FLOODING_OF_NON_RESPONSIVE);

        // Remove the previous flooding aircraft from the radar
        for (const aircraft of this.floodingAircrafts) {
            this._aircraftController.aircraft_remove(aircraft);
        }

        this.floodingAircrafts = [];

        // Create and add the new flooding aircraft
        for (let i = 0; i < amountOfAircraft; i++) {
            this._aircraftController.createNewFloodAircraft(i);
            const newAircraft = this._aircraftController.aircraft.list[this._aircraftController.aircraft.list.length - 1];
            this.floodingAircrafts.push(newAircraft);
        }

        console.log('Flooding of aircraft updated', amountOfAircraft);

        // Add option so no score is given for the flooding attack - done
        // Add option so no deduction is given for the flooding attack - done
        // Add so no score is deducted when removing the flooding aircraft - done
        // Change the color of the flooding aircraft
        // Add option to hide the score, always show when test is ended
        // Flooding should not respond to commands - done
    }

    /**
     * Force updates the score in the UI
     *
     * @param {Number} value
     */
    toggleDisplayOfScoreInUI(value) {
        GameController.game_updateScore(true);
    }

    /**
     * Returns the amount of aircraft affected by the given attack type
     *
     * @param {Number} attackType
     * @returns
     */
    getAmountOfAircraftAffectedByAttack(attackType) {
        let amnt = 0;
        for (const ac of this._aircraftController.aircraft.list) {
            if (ac.attackType === attackType) {
                amnt++;
            }
        }
        return amnt;
    }

    /**
     * Spawns sybil nodes for the given aircraft model
     *
     * @param {AircraftModel} aircraftModel
     */
    spawnSybilNodes(aircraftModel) {
        const MAX_SYBIL_NODES = this.amountOfSybilNodes;
        for (let i = 0; i < MAX_SYBIL_NODES; i++) {
            this._aircraftController.createSybilAttack(aircraftModel);
        }
    }

    getGNSSAttacks() {
        return this._gnssAttackController.getGNSSAttacks();
    }

    // Currently the user loses points when disabling GPS jamming, if they had not guessed it
    toggleGPSJamming(value) {
        console.log('Toggle GPS jamming, value: ', value);

        if (value) {
            this._gnssAttackController.setGNSSAttacks(this._buildRandomGNSSAttacks());
        } else {
            this._gnssAttackController.setGNSSAttacks([]);
        }
    }

    _attackIsTooClose(x, y, radius, attacks) {
        for (const attack of attacks) {
            const distance = Math.sqrt(Math.pow(attack.getX() - x, 2) + Math.pow(attack.getY() - y, 2));
            if (distance < (attack.getRadius() + radius) * 0.9) { // Allow a 10% overlap
                return true;
            }
        }
        return false;
    }

    _buildRandomGNSSAttacks() {
        console.log('Building random GNSS attacks. High: ', this.getAttackOption(ATTACK_SETTING_NAMES.GPS_JAMMING_HIGH_NR_ZONES),
            ' Low: ', this.getAttackOption(ATTACK_SETTING_NAMES.GPS_JAMMING_LOW_NR_ZONES));

        const numberOfHighPowerAttacks = this.getAttackOption(ATTACK_SETTING_NAMES.GPS_JAMMING_HIGH_NR_ZONES);
        let highPowerBuilt = 0;
        const numberOfLowPowerAttacks = this.getAttackOption(ATTACK_SETTING_NAMES.GPS_JAMMING_LOW_NR_ZONES);
        let lowPowerBuilt = 0;

        const attacks = [];

        let buildHighPower = true;

        let tries = 0;
        for (let i = 0; i < numberOfHighPowerAttacks + numberOfLowPowerAttacks; i++) {
            // Randomize the position of the attack between -300 and 300
            const x = Math.floor(Math.random() * 600) - 300;
            const y = Math.floor(Math.random() * 600) - 300;

            if (highPowerBuilt == numberOfHighPowerAttacks && buildHighPower) {
                buildHighPower = false;
            }
            if (lowPowerBuilt == numberOfLowPowerAttacks && !buildHighPower) {
                buildHighPower = true;
            }

            const radius = buildHighPower ? this.getAttackOption(ATTACK_SETTING_NAMES.GPS_JAMMING_HIGH_RADIUS) : this.getAttackOption(ATTACK_SETTING_NAMES.GPS_JAMMING_LOW_RADIUS);

            if (!this._attackIsTooClose(x, y, radius, attacks)) {
                if (buildHighPower) {
                    highPowerBuilt++;
                } else {
                    lowPowerBuilt++;
                }
                attacks.push(new GNSSAttackModel({
                    x: x,
                    y: y,
                    radius: radius,
                    highPower: buildHighPower
                }));
                buildHighPower = !buildHighPower;
            } else if (tries < 10) {
                // If the attack is too close to another one, try again
                i--;
                tries++;
            }
        }

        return attacks;
    }

    /**
     * Retrieves the list of drone attacks from the drone attack controller.
     * 
     * @returns {Array} An array of drone attack objects.
     */
    getDroneAttacks() {
        return this._droneAttackController.getDroneAttacks();
    }

    /**
     * Builds a drone attack configuration based on the provided aircraft model and attack options.
     *
     * @param {AircraftModel} aircraftModel - The model of the aircraft to base the drone attack on.
     * @returns {DroneAttackModel[]} An array containing the drone attack configuration.
     */
    _buildDroneAttack(aircraftModel) {
        const attacks = [];
        let x = null;
        let y = null;
        const radius =  this.getAttackOption(ATTACK_SETTING_NAMES.DRONE_ATTACK_RADIUS);
        let heading = this.getAttackOption(ATTACK_SETTING_NAMES.DRONE_ATTACK_HEADING);
        // Probability that the drone should move
        const shouldMove = Math.random() < this.getAttackOption(ATTACK_SETTING_NAMES.PROBABILITY_OF_MOVING_DRONE_ATTACK);
        const movingSpeed = this.getAttackOption(ATTACK_SETTING_NAMES.DRONE_ATTACK_MOVING_SPEED);
        const extraDespawnTime = this.getAttackOption(ATTACK_SETTING_NAMES.DRONE_ATTACK_EXTRA_DESPAWN_TIME);

        if (aircraftModel == null) {
            return;
        }
        else if (shouldMove) {
            heading = this.getAttackOption(ATTACK_SETTING_NAMES.DRONE_ATTACK_HEADING);
            // randomize drone heading if option is set to random
            if (heading === 0) {
                // random number between 1 and 4
                heading = Math.ceil(Math.random() * 4);
            }

            if (heading === 1) { // North West
                x = 150;
                y = -150;
            } else if (heading === 2) { // North East
                x = -150;
                y = -150;
            } else if (heading === 3) { // South West
                x = 150;
                y = 150;
            } else if (heading === 4) { // South East
                x = -150;
                y = 150;
            }

        } else {
            // Find the first waypoint that is close to the airport center
            const waypoints = aircraftModel.fms.waypoints;
            const minDistanceFromAirportCenter = this.getAttackOption(ATTACK_SETTING_NAMES.DRONE_ATTACK_MIN_DISTANCE_FROM_AIRPORT_CENTER);
            for (let i = 0; i < waypoints.length; i++) {
                if (Math.abs(waypoints[i].positionModel.relativePosition[0]) <= minDistanceFromAirportCenter &&
                    Math.abs(waypoints[i].positionModel.relativePosition[1]) <= minDistanceFromAirportCenter ) {
                    x = waypoints[i].positionModel.relativePosition[0];
                    y = waypoints[i].positionModel.relativePosition[1];
                    break;
                }
            }
        }
        
        // Add the drone attack to the list of attacks
        attacks.push(new DroneAttackModel({
            x: x,
            y: y,
            radius: radius,
            aircraftId: aircraftModel.id,
            isMoving: shouldMove,
            movingSpeed: movingSpeed,
            heading: heading,
            extraDespawnTime: extraDespawnTime
        }));

        return attacks;
    }
}

export default new AttackController();
